import React, { useMemo } from "react";
import { Route, useRouteMatch, Switch } from "react-router-dom";
import { Card, Flex, QueryLoader, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { ExistingPaymentPrefDisplayWithId } from "@jsq/payments-components";
import {
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "~/graphql/supergraphEnums";
import {
  useGetInvestorBankDetailsQuery,
  useGetPaymentAccountQuery,
  useGetSubscriptionAddressQuery,
} from "~/graphql/supergraphHooks";
import { toGlobalId } from "~/utils/graphql-helpers";
import BeneficiaryFullScreen from "./BeneficiaryFullScreen";
import BeneficiarySummary from "./BeneficiarySummary";
import CreatePaymentBeneficiary from "./CreatePaymentBeneficiary";
import CreatePaymentInstruction from "./CreatePaymentInstruction";
import EditPaymentBeneficiary from "./EditPaymentBeneficiary";
import ManualInstruction from "./ManualInstruction";
import { PaymentInstructionsProvider } from "./PaymentInstructionsContext";
import PaymentProfileCreationComponent from "./PaymentProfileCreationComponent";
import PaymentProfileEditComponent from "./PaymentProfileEditComponent";
import {
  buildCheckDisplayOverrideFromSubscriptionAccount,
  getAvailableProfiles,
  getDisplayPaymentAccountId,
} from "./utils";

type Props = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  programId: number;
  readonly: boolean;
  subscriptionId?: string;
};

const PaymentInstructions = ({
  caseId,
  caseType,
  programId,
  readonly,
  subscriptionId,
}: Props) => {
  const match = useRouteMatch();
  const bankDetailsResults = useGetInvestorBankDetailsQuery({
    variables: {
      caseId: caseId,
    },
    fetchPolicy: "network-only",
  });

  const subscriptionGlobalId = subscriptionId
    ? toGlobalId("SubscriptionV2ObjectType", subscriptionId)
    : "";

  const { data: subscriptionAddressResults } = useGetSubscriptionAddressQuery({
    variables: {
      subscriptionId: subscriptionGlobalId,
    },
    fetchPolicy: "network-only",
    skip: !subscriptionId,
  });

  const complianceCase = useMemo(
    () => bankDetailsResults?.data?.complianceCase,
    [bankDetailsResults?.data?.complianceCase],
  );

  const paymentAccountId = useMemo(
    () =>
      getDisplayPaymentAccountId(
        caseType,
        complianceCase?.paymentAccount?.id,
        complianceCase?.investingProfile ?? null,
        complianceCase?.profiles,
      ),
    [
      caseType,
      complianceCase?.paymentAccount?.id,
      complianceCase?.investingProfile,
      complianceCase?.profiles,
    ],
  );

  const paymentAccountResults = useGetPaymentAccountQuery({
    variables: {
      id: paymentAccountId ?? 0,
      programId: programId,
    },
    fetchPolicy: "network-only",
    skip: !paymentAccountId,
  });

  const { investor, profiles } = useMemo(
    () => getAvailableProfiles(complianceCase),
    [complianceCase],
  );

  const paymentBeneficiaryProfile = useMemo(
    () => paymentAccountResults?.data?.compliancePaymentAccount?.profile ?? null,
    [paymentAccountResults?.data?.compliancePaymentAccount?.profile],
  );

  const paymentDataProfiles = useMemo(() => {
    return paymentBeneficiaryProfile ? [paymentBeneficiaryProfile] : [];
  }, [paymentBeneficiaryProfile]);

  const hasPaymentInstructions = useMemo(
    () => !!paymentAccountResults?.data?.compliancePaymentAccount,
    [paymentAccountResults?.data?.compliancePaymentAccount],
  );

  const paymentPrefId = useMemo(() => {
    const prefId = complianceCase?.paymentAccount?.paymentPrefId;
    if (prefId) {
      return prefId;
    }
    // As a fallback we are reading from the external objects for backwards compatibility. We could remove this once we are sure all cases have a payment pref id.
    const externalObject = complianceCase?.externalObjects?.find(
      (obj) => obj.objectTypeEnum === ComplianceExternalObjectType.PaymentPreference,
    );
    if (externalObject) {
      return Number(externalObject.objectId);
    }
    return undefined;
  }, [complianceCase?.paymentAccount?.paymentPrefId, complianceCase?.externalObjects]);

  const accountHolderName: string = useMemo(() => {
    return complianceCase?.paymentAccount?.payeeName
      ? complianceCase?.paymentAccount?.payeeName
      : "";
  }, [complianceCase?.paymentAccount?.payeeName]);

  const paymentContent = useMemo(() => {
    if (paymentPrefId) {
      const checkDisplayOverride = buildCheckDisplayOverrideFromSubscriptionAccount(
        subscriptionAddressResults,
      );

      return (
        <ExistingPaymentPrefDisplayWithId
          paymentPrefId={toGlobalId("PaymentPrefObjectType", paymentPrefId)}
          displayRevealDetails={true}
          defaultExpanded={true}
          visibleFields={{
            ach: ["bankName", "accountName", "additionalInstructions"],
            wire: ["beneficiaryName", "bank", "wireObi"],
            check: ["payeeName"],
          }}
          showBankDomicile={true}
          isFullWidth={false}
          checkDisplayOverride={checkDisplayOverride}
        />
      );
    }

    if (hasPaymentInstructions) {
      return (
        <ManualInstruction
          caseId={caseId}
          paymentAccount={paymentAccountResults.data!.compliancePaymentAccount!}
          readonly={readonly}
        />
      );
    }

    return <CreatePaymentInstruction />;
  }, [
    paymentPrefId,
    hasPaymentInstructions,
    caseId,
    readonly,
    subscriptionAddressResults,
    paymentAccountResults.data,
  ]);

  if (!caseId) {
    return <Text variant="regular-md">Case not found</Text>;
  }

  return (
    <QueryLoader result={bankDetailsResults}>
      <>
        <PaymentInstructionsProvider
          caseId={caseId}
          caseType={caseType}
          programId={programId}
          readonly={readonly}
          paymentPrefId={paymentPrefId}
          paymentAccountId={paymentAccountId}
          investor={investor}
          profiles={profiles}
          paymentDataProfiles={paymentDataProfiles}
        >
          <Flex flexDirection="column" gap={Sizes.LG}>
            <Card>
              <Text variant="secondary-md" marginBottom="lg">
                PAYMENT INSTRUCTIONS
              </Text>
              {paymentContent}
            </Card>
            {(paymentPrefId || (hasPaymentInstructions && paymentDataProfiles.length > 0)) && (
              <Card>
                <Text variant="secondary-md" marginBottom="lg">
                  PAYMENT BENEFICIARY
                </Text>
                <BeneficiarySummary
                  caseId={caseId}
                  paymentDataProfiles={paymentDataProfiles}
                  readonly={readonly}
                  paymentPrefId={paymentPrefId}
                />
              </Card>
            )}
          </Flex>
          <Switch>
            <Route
              path={`${match.path}/create`}
              exact
              render={() => <CreatePaymentBeneficiary />}
            />
            <Route
              path={`${match.path}/add/:beneficiaryId`}
              exact
              render={() => (
                <EditPaymentBeneficiary
                  title="Add payment beneficiary"
                  sourceUrl={`${match.path}`}
                />
              )}
            />
            <Route
              path={`${match.path}/edit/:beneficiaryId`}
              exact
              render={() => (
                <EditPaymentBeneficiary
                  title="Edit payment beneficiary"
                  sourceUrl={`${match.path}`}
                />
              )}
            />
            <Route
              path={[
                `${match.path}/create/profile`,
                `${match.path}/edit/:beneficiaryId/profile`,
                `${match.path}/add/:beneficiaryId/profile`,
              ]}
              render={() => (
                <PaymentProfileCreationComponent
                  caseId={caseId}
                  investingProfileId={investor?.id}
                />
              )}
            />
            <Route
              path={[
                `${match.path}/create/profile-edit/:profileId`,
                `${match.path}/edit/:beneficiaryId/profile-edit/:profileId`,
                `${match.path}/add/:beneficiaryId/profile-edit/:profileId`,
              ]}
              exact
              render={() => (
                <PaymentProfileEditComponent caseId={caseId} programId={programId} />
              )}
            />
            <Route
              path={[
                `${match.path}/add-full-screen/:paymentAccountId/create/profile`,
                `${match.path}/edit-full-screen/:paymentAccountId/create/profile`,
              ]}
              render={() => (
                <PaymentProfileCreationComponent
                  caseId={caseId}
                  investingProfileId={investor?.id}
                />
              )}
            />
            <Route
              path={[
                `${match.path}/add-full-screen/:paymentAccountId/edit/profile/:profileId`,
                `${match.path}/edit-full-screen/:paymentAccountId/edit/profile/:profileId`,
              ]}
              exact
              render={() => (
                <PaymentProfileEditComponent caseId={caseId} programId={programId} />
              )}
            />
            <Route
              path={`${match.path}/add-full-screen/:paymentAccountId`}
              exact
              render={() => (
                <BeneficiaryFullScreen
                  title="Add payment beneficiary"
                  accountHolderName={accountHolderName}
                />
              )}
            />
            <Route
              path={`${match.path}/edit-full-screen/:paymentAccountId`}
              exact
              render={() => (
                <BeneficiaryFullScreen
                  title="Edit payment beneficiary"
                  accountHolderName={accountHolderName}
                />
              )}
            />
          </Switch>
        </PaymentInstructionsProvider>
      </>
    </QueryLoader>
  );
};

export default PaymentInstructions;
